import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';

const BASE_URL = 'http://localhost:5001';
const WhatsAppContext = createContext(null);

export function WhatsAppProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState('disconnected');
  const [contacts, setContacts] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [incomingLog, setIncomingLog] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [myStatus, setMyStatus] = useState(null);

  // Keep activeChat in a ref so socket callbacks always see the latest value
  const activeChatRef = useRef(activeChat);
  activeChatRef.current = activeChat;

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/statuses`);
      setStatuses(res.data);
      const own = res.data.find(s => s.userJid === 'me@s.whatsapp.net');
      setMyStatus(own || null);
    } catch (err) {
      console.error('Fetch statuses failed:', err);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      await new Promise(r => setTimeout(r, 800));
      const res = await axios.get(`${BASE_URL}/contacts`);
      if (Array.isArray(res.data)) {
        setContacts(res.data);
      }
    } catch (err) {
      console.error('Load contacts failed:', err);
    }
  }, []);

  useEffect(() => {
    const newSocket = io(BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
    });

    newSocket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
    });

    newSocket.on('qr', (qrImage) => {
      if (qrImage) {
        setQr(qrImage);
        setStatus('scan_qr');
      } else {
        setQr(null);
      }
    });

    newSocket.on('status', (s) => {
      console.log('📡 Status update:', s);
      setStatus(s);
      if (s === 'connected') {
        setQr(null);
        loadContacts();
        fetchStatuses();
      }
      if (s === 'disconnected' || s === 'logged_out') {
        setQr(null);
      }
    });

    newSocket.on('new_message', (msg) => {
      if (!msg || !msg.from) return;

      setMessages(prev => {
        const chatId = msg.from;
        const existing = prev[chatId] || [];
        // Avoid duplicates
        if (existing.find(m => m.id === msg.id)) return prev;
        return {
          ...prev,
          [chatId]: [...existing, msg],
        };
      });

      // Only add to incoming log if it's not from me
      if (!msg.isMine) {
        setIncomingLog(prev => [msg, ...prev].slice(0, 200));
      }

      // Increment unread count if not the active chat
      if (msg.from !== activeChatRef.current && !msg.isMine) {
        setUnreadCounts(prev => ({
          ...prev,
          [msg.from]: (prev[msg.from] || 0) + 1,
        }));
      }

      // Add sender to contacts if not already there
      setContacts(prev => {
        const exists = prev.find(c => c.jid === msg.from);
        const realName = msg.senderName || msg.from.split('@')[0];
        if (!exists) {
          return [...prev, { jid: msg.from, name: realName }];
        } else if (
          msg.senderName &&
          exists.name === exists.jid.split('@')[0]
        ) {
          return prev.map(c =>
            c.jid === msg.from ? { ...c, name: realName } : c
          );
        }
        return prev;
      });
    });

    newSocket.on('message_receipt', (receipt) => {
      if (!receipt || !receipt.jid) return;
      setMessages(prev => {
        const chatId = receipt.jid;
        if (!prev[chatId]) return prev;
        return {
          ...prev,
          [chatId]: prev[chatId].map(m =>
            m.id === receipt.messageId ? { ...m, status: receipt.status } : m
          ),
        };
      });
    });

    newSocket.on('status_updated', () => {
      fetchStatuses();
    });

    return () => {
      newSocket.removeAllListeners();
      newSocket.close();
    };
  }, [loadContacts, fetchStatuses]);

  const selectContact = useCallback(async (jid) => {
    setActiveChat(jid);
    activeChatRef.current = jid;
    setUnreadCounts(prev => ({ ...prev, [jid]: 0 }));

    // Load messages if not already loaded
    try {
      const res = await axios.get(`${BASE_URL}/messages/${jid}`);
      if (Array.isArray(res.data)) {
        setMessages(prev => ({
          ...prev,
          [jid]: res.data,
        }));
      }
    } catch (err) {
      console.error('Load messages failed:', err);
    }
  }, []);

  const addSentMessage = useCallback((jid, msg) => {
    setMessages(prev => {
      const existing = prev[jid] || [];
      // Avoid duplicates
      if (existing.find(m => m.id === msg.id)) return prev;
      return {
        ...prev,
        [jid]: [...existing, { ...msg, status: 'sent' }],
      };
    });
  }, []);

  const postStatus = useCallback(async (formData) => {
    try {
      const res = await axios.post(`${BASE_URL}/status`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMyStatus(res.data);
      fetchStatuses();
      return { success: true };
    } catch (err) {
      console.error('Post status failed:', err);
      return { success: false, error: err.message };
    }
  }, [fetchStatuses]);

  const refreshContacts = useCallback(() => {
    loadContacts();
  }, [loadContacts]);

  const value = {
    socket,
    qr,
    status,
    contacts,
    setContacts,
    activeChat,
    messages,
    setMessages,
    unreadCounts,
    setUnreadCounts,
    incomingLog,
    setIncomingLog,
    selectContact,
    addSentMessage,
    setQr,
    statuses,
    myStatus,
    postStatus,
    refreshContacts,
    BASE_URL,
  };

  return (
    <WhatsAppContext.Provider value={value}>
      {children}
    </WhatsAppContext.Provider>
  );
}

export const useWhatsApp = () => {
  const ctx = useContext(WhatsAppContext);
  if (!ctx) throw new Error('useWhatsApp must be used within WhatsAppProvider');
  return ctx;
};
